/** Where a figure was read from, beside the figure — **ADR-0045, option B, the screen half.**
 *
 * DOC-03's remaining clause is *"a user corrects a date while viewing its source"*, and the half
 * that was missing was the *viewing*. The backend records where in a document's text each field
 * was read and re-checks that position before serving it; this is the panel that shows it, and the
 * only thing it draws is a string EmitIQ's own code produced, escaped like any other text.
 *
 * **It is not the document, and it says so in the server's words.** ADR-0045: *"a panel that looks
 * like the document while being a transcription is worse than one that says 'this is the text
 * EmitIQ read from page 3'."* The caption comes from `spans.PANE_CAPTION` on the server, like every
 * other sentence here, because a sentence improved today has to apply to an extraction run last
 * year — which a sentence compiled into a bundle does not.
 *
 * Five things this panel refuses to do. Each is a defect somebody would reintroduce as a kindness.
 *
 * - **It never draws a highlight for anything but `verified`.** The other four statuses carry no
 *   coordinates and the server refuses to emit any, and `drawn` refuses again here — because a
 *   client that trusted the numbers over the word is the F10 failure in a new place.
 * - **It never searches the text for the value.** That is *relocation*, and ADR-0045 decided
 *   against it: a unique match somewhere else is a second answer with a different trust level,
 *   wearing the first one's clothes. `SourceText.test.tsx`'s *"never looks for a value it was not
 *   told where to find"* is what fails if somebody adds it — and it is driven against a fixture
 *   whose text contains the value **twice**, so a pane that searched would find it.
 * - **It never reads an empty `spans` array as "all well".** It is never empty for a document with
 *   an extraction — every schema field gets a row — so an empty one means something unexpected,
 *   and `any([])` is `False` is this repository's most-repeated defect (F07, the nine read hooks,
 *   `unanchored_entries`). The status is the answer; the length of the list is not.
 * - **It never renders an empty box on a refusal.** A scan has no text layer, and ADR-0028's
 *   refusal reaches the reader with its code and its remedy (DOC-01). The honest next step is the
 *   supplier's original, **not** an "open the document" control: nothing serves a presigned URL in
 *   any environment (**G-04**), and offering one would be a control that predictably fails.
 * - **It never says the document changed.** `reproduced: false` means *EmitIQ's reading* has moved
 *   — a different reader version, a change to what is removed before reading. Two different
 *   claims, one of them false, so the sentence is the server's (`reading_note`) rather than a
 *   template's.
 *
 * **The point of the whole slice is the correction loop**, so the pane is a reader and never a
 * writer. `onSelect` moves the highlight; the correction is made by the control that already
 * exists on the review screen, which appends, requires a reason and merges once in promotion. A
 * second write path from here is how two screens come to disagree about what a correction is.
 */

import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";

import { useDocumentText } from "../api/evidence";
import type { DocumentText, ExtractedFieldSpan } from "../api/evidence";
import { Refused } from "../api/refusal";
import { ReadFailed } from "../components/Loaded";
import { InfoPopover } from "../components/ui";
import { Empty, Panel } from "../components/Panel";
import { type Drawn, drawn, fieldLabel } from "./fields";

import own from "./SourceText.module.css";
import styles from "./Screen.module.css";


/** What the pane may say about a field when the caller knows it: the value as read, and how sure. */
export interface FieldDetail {
  /** The field's name in a person's words. */
  label: string;
  /** The value as read, with its unit. Empty when the review has not been read. */
  value: string;
  /** Confidence in words (`97%`, `corrected by a person`). Empty when not known. */
  confidence: string;
}

/** One run drawn on a line. The selected one is the persistent highlight; the others are quiet marks
 * that exist only when the caller asked for every drawable position (`markAll`). */
interface Mark {
  field: string;
  at: Drawn;
  selected: boolean;
}

interface Tip {
  field: string;
  left: number;
  top: number;
}

/** The runs of one line, in order and never overlapping: a run that starts inside the one before it
 * is dropped rather than drawn on top, because two overlapping highlights are a third claim about
 * which characters a field was read from. The selected run wins a tie. */
function runsOf(marks: readonly Mark[]): Mark[] {
  const sorted = [...marks].sort(
    (a, b) => a.at.start - b.at.start || Number(b.selected) - Number(a.selected),
  );
  const kept: Mark[] = [];
  let reached = 0;
  for (const mark of sorted) {
    if (mark.at.start < reached) continue;
    kept.push(mark);
    reached = mark.at.start + mark.at.length;
  }
  return kept;
}

/** One line of the page, with its marked runs.
 *
 * The slice is taken by **index**, never by searching for a value. The indices come from the
 * server, which derived them from this very text and re-checked them against it before answering;
 * finding the value some other way would be relocation under a different name.
 */
function Line({
  body,
  marks,
  details,
  onMark,
  onTip,
}: {
  body: string;
  marks: readonly Mark[];
  details?: Readonly<Record<string, FieldDetail>>;
  onMark?: (field: string) => void;
  onTip?: (tip: Tip | null) => void;
}) {
  const runs = runsOf(marks);
  if (runs.length === 0) return <span className={own.line}>{body || " "}</span>;
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (const run of runs) {
    // Clamped, because a span that ran off the end of a line it no longer fits would otherwise draw
    // a highlight over nothing and read as a correct one. The server checks this; so does this.
    const start = Math.min(Math.max(run.at.start, cursor), body.length);
    const end = Math.min(start + run.at.length, body.length);
    if (end <= start) continue;
    parts.push(body.slice(cursor, start));
    const detail = details?.[run.field];
    const said = detail
      ? [detail.label, detail.value, detail.confidence ? `confidence ${detail.confidence}` : ""]
          .filter(Boolean)
          .join(", ")
      : "";
    const showTip = (el: HTMLElement) =>
      onTip?.({ field: run.field, left: el.offsetLeft, top: el.offsetTop + el.offsetHeight + 2 });
    parts.push(
      <mark
        key={`${run.field}-${String(start)}`}
        className={run.selected ? own.mark : own.quiet}
        data-testid={run.selected ? "source-highlight" : "source-mark"}
        data-field={run.field}
        {...(onMark
          ? {
              role: "button" as const,
              tabIndex: 0,
              "aria-label": said ? `${said}. Show its row.` : "Show its row.",
              onClick: () => onMark(run.field),
              onKeyDown: (event: KeyboardEvent) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onMark(run.field);
                }
              },
            }
          : {})}
        onMouseEnter={(event) => showTip(event.currentTarget)}
        onFocus={(event) => showTip(event.currentTarget)}
        onMouseLeave={() => onTip?.(null)}
        onBlur={() => onTip?.(null)}
      >
        {body.slice(start, end)}
      </mark>,
    );
    cursor = end;
  }
  parts.push(body.slice(cursor));
  return <span className={own.line}>{parts}</span>;
}

function Body({
  page,
  marks,
  details,
  onMark,
}: {
  page: { body: string };
  marks: readonly Mark[];
  details?: Readonly<Record<string, FieldDetail>>;
  onMark?: (field: string) => void;
}) {
  const lines = page.body.split("\n");
  const ref = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  const selected = marks.find((mark) => mark.selected)?.field;
  // The persistent highlight is brought into view when it is chosen: a link elsewhere on the page
  // that says "show me on the bill" has not answered until the characters are on screen.
  useEffect(() => {
    if (!selected) return;
    // `?.` because jsdom has no scrollIntoView; a browser always does.
    ref.current?.querySelector('[data-testid="source-highlight"]')?.scrollIntoView?.({ block: "nearest" });
  }, [selected]);
  const said = tip ? details?.[tip.field] : undefined;
  return (
    <div className={own.body} data-testid="source-body" ref={ref}>
      {lines.map((line, index) => (
        // The index **is** the identity here: these are lines of one immutable string, and the
        // server's span is an index into exactly this list, so nothing can reorder beneath it.
        <Line
          key={index}
          body={line}
          marks={marks.filter((mark) => mark.at.line === index)}
          details={details}
          onMark={onMark}
          onTip={details ? setTip : undefined}
        />
      ))}
      {tip && said ? (
        <span className={own.tip} role="tooltip" data-testid="source-tip" style={{ left: tip.left, top: tip.top }}>
          <b>{said.label}</b>
          {said.value ? <span>{said.value}</span> : null}
          {said.confidence ? <span>Confidence {said.confidence}</span> : null}
        </span>
      ) : null}
    </div>
  );
}

/** What can and cannot be pointed at, with the server's sentence for each. */
function Answers({
  spans,
  selected,
  onSelect,
  pointers,
}: {
  spans: readonly ExtractedFieldSpan[];
  selected: string | null;
  onSelect: (field: string | null) => void;
  /** Whether a position is a control. Off where the caller's own field list already carries one (the
   * review screen's "Show me where"), so a field never has two buttons for one act. */
  pointers: boolean;
}) {
  // **Never `spans.length === 0` as "all well".** For a document with an extraction this list has
  // one row per schema field, so an empty one is not "nothing to flag" — it is nothing known.
  if (spans.length === 0) {
    return (
      <p className={styles.error} data-testid="source-nothing-known">
        EmitIQ holds no record of where any figure on this document was read from. That is not the
        same as every figure being confirmed — it is the absence of the record, and it means no
        position on this page can be shown.
      </p>
    );
  }

  const drawable = spans.filter((span) => drawn(span) !== null);
  return (
    <>
      <p className={own.tally} data-testid="source-tally">
        <span className="num">{drawable.length}</span> of{" "}
        <span className="num">{spans.length}</span> fields can be pointed at in this text.
      </p>
      <ul className={own.answers}>
        {spans.map((span) => {
          const position = drawn(span);
          return (
            <li
              key={span.field}
              className={`${own.answer} ${selected === span.field ? own.answerHere : ""}`}
              data-testid={`source-answer-${span.field}`}
              data-status={span.status}
            >
              <span className={own.answerName}>{fieldLabel(span.field)}</span>
              {position && !pointers ? (
                <span className={own.answerWhy}>Read on page {position.page}</span>
              ) : position ? (
                <button
                  type="button"
                  className={styles.fixLink}
                  onClick={() => onSelect(selected === span.field ? null : span.field)}
                >
                  {selected === span.field
                    ? "Hide where it was read"
                    : `Show where it was read · page ${position.page}`}
                </button>
              ) : (
                /* **No control**, because there is nothing it could do (design rule 6). The
                   sentence is the server's: it says what is and is not known, and a status with no
                   words would render as a blank beside a figure. */
                <span className={own.answerWhy}>{span.sentence}</span>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** The refusal, with ADR-0028's own code and remedy — **never an empty box.**
 *
 * `error.message` already carries the server's sentence and its remedy joined (`refusalFrom`), so
 * nothing here rewrites either. What this adds is the one thing the server cannot say: that the
 * alternative ADR-0045 names for a scan — option A, the original out of origin — **is not
 * available**, because nothing serves a presigned URL in any environment (G-04). Saying so is the
 * requirement; offering a link would be a control that predictably fails.
 */
function Refusal({ error }: { error: Refused }) {
  const noText = error.code === "no_text_layer" || error.code === "needs_ocr";
  return (
    <div role="alert" data-testid="source-refused" data-code={error.code || "unknown"}>
      <p className={styles.error}>{error.message}</p>
      {noText ? (
        <p className={styles.note}>
          There is nothing for EmitIQ to show here, and no way to look at the file from inside
          EmitIQ either — the stored original is served from outside this application and that is
          not available yet. A figure from this document has to be checked against the copy you hold.
        </p>
      ) : null}
    </div>
  );
}

/** The panel. Controlled: the parent owns which field is highlighted.
 *
 * Controlled rather than self-owning, because the review screen has the **same** selection in two
 * places — the field list and this list — and two copies of one piece of state is how a screen
 * comes to highlight one field while saying it is showing another.
 */
export function SourceTextPane({
  documentId,
  selected,
  onSelect,
  column = "1 / 13",
  enabled = true,
  markAll = false,
  details,
  onMark,
  accepted = false,
  pointers = true,
}: {
  documentId: string | undefined;
  selected: string | null;
  onSelect: (field: string | null) => void;
  column?: string;
  enabled?: boolean;
  /** Draw every position the server confirmed, quietly, with the chosen one in full. Off by default:
   * the review screen shows one highlight at a time. */
  markAll?: boolean;
  /** What each field was read as, for the hover. Without it a mark says nothing on hover. */
  details?: Readonly<Record<string, FieldDetail>>;
  /** A mark was pressed. The caller scrolls its own row into view; the pane never writes. */
  onMark?: (field: string) => void;
  /** The document has been accepted by a person. Then "nobody has read it" would contradict the
   * state beside it: its figures were entered at review, and no text positions were recorded. */
  accepted?: boolean;
  /** Whether the list of what can be pointed at carries a control per field. Default on; the review
   * screen turns it off because each of its rows already has "Show me where". */
  pointers?: boolean;
}) {
  const read = useDocumentText(documentId, enabled);
  /** Which page the reader is browsing, when no highlight is deciding for them. */
  const [browsing, setBrowsing] = useState(1);

  //: **A pane told not to ask has nothing to wait for.** A disabled query is `pending` for ever, so
  //: without this a document the caller knows nobody has read would say "Reading the document…" and
  //: never stop; it says what a 404 says.
  if (read.isPending && read.fetchStatus !== "idle") {
    return (
      <Panel title="Where it was read from" column={column}>
        <Empty>Reading the document…</Empty>
      </Panel>
    );
  }

  // **A refusal is not a failed read, and neither is an empty pane.** Three branches, three
  // different sentences — this is EXP-29's lesson, and the 422 is the one ADR-0045 asks for by
  // name: *"it shows nothing and says so … rather than being hidden behind an empty box."*
  if (read.error instanceof Refused && read.error.status === 422) {
    return (
      <Panel title="Where it was read from" sub="nothing to show" column={column}>
        <Refusal error={read.error} />
      </Panel>
    );
  }
  if (read.error) {
    return (
      <Panel title="Where it was read from" column={column}>
        <ReadFailed
          of="The text EmitIQ read from this document"
          error={read.error}
          onRetry={() => void read.refetch()}
        />
      </Panel>
    );
  }
  if (!read.data) {
    return (
      <Panel title="Where it was read from" sub={accepted ? "no positions recorded" : "not read yet"} column={column}>
        <p className={styles.note} data-testid="source-not-read">
          {accepted
            ? "Figures were entered at review from this document; no text positions were recorded, so none can be shown on it."
            : "Nobody has read this document yet, so there is nothing recorded about where a figure on it came from. Reading it is the step before review."}
        </p>
      </Panel>
    );
  }

  return (
    <Pane
      text={read.data}
      selected={selected}
      onSelect={onSelect}
      column={column}
      browsing={browsing}
      onBrowse={setBrowsing}
      markAll={markAll}
      details={details}
      onMark={onMark}
      pointers={pointers}
    />
  );
}

/** The runs to draw on one page: the chosen field's, and with `markAll` every other confirmed one.
 * Every position passes through `drawn`, the single gate, so nothing unconfirmed is ever marked. */
function marksOn(text: DocumentText, page: number, selected: string | null, markAll: boolean): Mark[] {
  const out: Mark[] = [];
  for (const span of text.spans) {
    const at = drawn(span);
    if (!at || at.page !== page) continue;
    const isSelected = span.field === selected;
    if (isSelected || markAll) out.push({ field: span.field, at, selected: isSelected });
  }
  return out;
}

function Pane({
  text,
  selected,
  onSelect,
  column,
  browsing,
  onBrowse,
  markAll,
  details,
  onMark,
  pointers,
}: {
  text: DocumentText;
  selected: string | null;
  onSelect: (field: string | null) => void;
  column: string;
  browsing: number;
  onBrowse: (page: number) => void;
  markAll: boolean;
  details?: Readonly<Record<string, FieldDetail>>;
  onMark?: (field: string) => void;
  pointers: boolean;
}) {
  const highlight = drawn(text.spans.find((span) => span.field === selected));
  // **The highlight decides the page when there is one.** Derived rather than stored, for the same
  // reason nothing in this product stores `is_current`: a second copy of "which page" would
  // eventually disagree, and the copy that won would draw a highlight on a page nobody is looking
  // at. Browsing away drops the selection instead (`onBrowse` below), which is the honest
  // direction: a reader who turns the page is no longer looking at that field.
  const shown = highlight ? highlight.page : Math.min(Math.max(browsing, 1), text.pages.length);
  const page = text.pages[shown - 1];

  return (
    <Panel
      title="Where it was read from"
      sub={`page ${shown} of ${text.pages.length}`}
      column={column}
    >
      {/* **The server's sentence, not ours.** It says which page this is, how many there are, and
          that this is not the document — a claim about what the product did, so the product makes
          it. See `spans.PANE_CAPTION`. */}
      <p className={own.caption} data-testid="source-caption">
        {page?.caption ?? ""}
      </p>

      {/* `reproduced: false`. **Our reading has moved; the document has not** — two claims, and
          only one is true. The words are the server's so a template cannot swap them. */}
      {text.reproduced ? null : (
        <p className={styles.error} role="status" data-testid="source-reading-moved">
          {text.reading_note}
        </p>
      )}

      {text.pages.length > 1 ? (
        <nav className={own.pager} aria-label="Pages of the text EmitIQ read">
          {text.pages.map((entry) => (
            <button
              key={entry.page}
              type="button"
              className={`${own.pageButton} ${entry.page === shown ? own.pageHere : ""}`}
              aria-current={entry.page === shown ? "page" : undefined}
              onClick={() => {
                onBrowse(entry.page);
                // Turning the page drops the highlight rather than carrying it: a highlight on a
                // page the reader navigated away from would be drawn on the wrong page or not at
                // all, and both are worse than none.
                onSelect(null);
              }}
            >
              Page {entry.page}
            </button>
          ))}
        </nav>
      ) : null}

      {page ? (
        <Body
          page={page}
          marks={marksOn(text, shown, selected, markAll)}
          details={details}
          onMark={onMark}
        />
      ) : (
        <Empty>This page has no text.</Empty>
      )}

      <Answers spans={text.spans} selected={selected} onSelect={onSelect} pointers={pointers} />

      <InfoPopover infoKey="processing.source-text" />
    </Panel>
  );
}
